import { useEffect, useRef, useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { fileUrl } from '../lib/api.js';
import { photoName, prepareUpload } from '../lib/files.js';
import { newKey } from '../lib/outbox.js';
import { fotoAblegen, fotoLaden, fotoLoeschen } from '../lib/fotospeicher.js';

const MAX = 30;

/** Vorschaubild: lokal aus dem Gerät, sonst aus den Dateien des Auftrags. */
function Vorschau({ foto }) {
  const [src, setSrc] = useState(foto.lokal ? null : fileUrl(foto.dateiId));
  useEffect(() => {
    if (!foto.lokal) return undefined;
    let aktiv = true;
    fotoLaden(foto.id).then((f) => { if (aktiv && f) setSrc(`data:image/jpeg;base64,${f.base64}`); }).catch(() => {});
    return () => { aktiv = false; };
  }, [foto.lokal, foto.id]);
  return src
    ? <img src={src} alt={foto.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    : <span className="sub" style={{ fontSize: 11, padding: 4 }}>{foto.name}</span>;
}

/**
 * Fotos zum Formular: Kamera oder Galerie. Ein neues Foto liegt sofort auf dem Gerät (auch ohne Netz) und geht beim
 * Speichern des Formulars in die Dateien des Auftrags (Ordner aus der Vorlage, sonst «Fotos»).
 * @param {{ f: object, wert: object[], onChange: (fn: (alt: object[]) => object[]) => void }} p
 */
export function FotosFeld({ f, wert, onChange }) {
  const kamera = useRef(null);
  const galerie = useRef(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  const liste = Array.isArray(wert) ? wert : [];
  const voll = liste.length >= MAX;

  const hinzufuegen = async (e) => {
    const dateien = [...(e.target.files ?? [])].slice(0, MAX - liste.length);
    e.target.value = '';
    if (!dateien.length) return;
    setFehler('');
    setLaedt(true);
    try {
      const neu = [];
      for (const [i, datei] of dateien.entries()) {
        const up = await prepareUpload(datei);
        const id = newKey();
        const basis = photoName(f.praefix || 'Foto');
        const name = dateien.length > 1 ? basis.replace(/\.jpg$/, `_${i + 1}.jpg`) : basis;
        await fotoAblegen({ id, fileName: name, base64: up.base64 });
        neu.push({ id, name, lokal: true, ordner: f.ordner || 'Fotos' });
      }
      onChange((alt) => [...(Array.isArray(alt) ? alt : []), ...neu].slice(0, MAX));
    } catch (err) {
      setFehler(err?.message || 'Foto konnte nicht gespeichert werden');
    } finally {
      setLaedt(false);
    }
  };

  const entfernen = (foto) => {
    if (foto.lokal) fotoLoeschen(foto.id).catch(() => {});
    onChange((alt) => (alt ?? []).filter((x) => (foto.lokal ? x.id !== foto.id : x.dateiId !== foto.dateiId)));
  };

  const wartend = liste.filter((x) => x.lokal).length;
  return (
    <div className="stack" style={{ gap: 8 }}>
      <span className="feldlabel">{f.titel}{liste.length ? ` (${liste.length})` : ''}</span>
      {liste.length > 0 && (
        <ul aria-label={f.titel} style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))', gap: 8 }}>
          {liste.map((foto) => (
            <li key={foto.lokal ? foto.id : foto.dateiId} style={{ position: 'relative', aspectRatio: '1', borderRadius: 10, overflow: 'hidden', background: 'var(--surface-2, #eee)', display: 'grid', placeItems: 'center' }}>
              {foto.lokal
                ? <Vorschau foto={foto} />
                : <a href={fileUrl(foto.dateiId)} target="_blank" rel="noreferrer" aria-label={`Foto ${foto.name} öffnen`} style={{ width: '100%', height: '100%' }}><Vorschau foto={foto} /></a>}
              <button type="button" aria-label={`Foto ${foto.name} entfernen`} onClick={() => entfernen(foto)}
                style={{ position: 'absolute', top: 4, right: 4, width: 32, height: 32, borderRadius: 16, border: 0, background: 'rgba(0,0,0,.6)', color: '#fff', display: 'grid', placeItems: 'center' }}>
                <Icon name="x" size={16} />
              </button>
              {foto.lokal && <span style={{ position: 'absolute', left: 4, bottom: 4, fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 6, background: 'rgba(0,0,0,.6)', color: '#fff' }}>auf Gerät</span>}
            </li>
          ))}
        </ul>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
        <button type="button" className="btn secondary" style={{ padding: '0 10px' }} onClick={() => kamera.current?.click()} disabled={laedt || voll}><Icon name="camera" />Kamera</button>
        <button type="button" className="btn secondary" style={{ padding: '0 10px' }} onClick={() => galerie.current?.click()} disabled={laedt || voll}><Icon name="image" />Galerie</button>
      </div>
      <input ref={kamera} type="file" accept="image/*" capture="environment" hidden onChange={hinzufuegen} data-testid={`${f.id}-kamera`} />
      <input ref={galerie} type="file" accept="image/*" multiple hidden onChange={hinzufuegen} data-testid={`${f.id}-galerie`} />
      {laedt && <span className="sub" style={{ fontSize: 13 }}>Foto wird vorbereitet …</span>}
      {voll && <span className="sub" style={{ fontSize: 13 }}>Höchstens {MAX} Fotos pro Formular.</span>}
      {wartend > 0 && <span className="sub" style={{ fontSize: 13 }}>{wartend === 1 ? '1 Foto liegt' : `${wartend} Fotos liegen`} auf dem Gerät und {wartend === 1 ? 'geht' : 'gehen'} beim Speichern in die Dateien des Auftrags (Ordner «{f.ordner || 'Fotos'}»).</span>}
      {fehler && <div className="notice err" role="alert">{fehler}</div>}
    </div>
  );
}
